import { useEffect, useRef, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog/AppDialog';
import { checkFormula, labelOf, type BuilderField, type FormulaResult } from './builderApi';

/** The prototype's operator list (inserted at the cursor), with its hints. */
const OPS: [string, string][] = [
  ['+', 'add'], ['-', 'subtract'], ['*', 'multiply'], ['/', 'divide'], ['(', ''], [')', ''],
  ['=', 'equals'], ['<>', 'not equal'], ['>', ''], ['>=', ''], ['<', ''], ['<=', ''],
  [' AND ', ''], [' OR ', ''], ['100', ''], ['1000', ''],
];
const CHECK_DELAY_MS = 250;

interface Props {
  fields: BuilderField[];
  index: number;
  kind: 'calc' | 'accept';
  readOnly?: boolean;
  onCancel: () => void;
  /** '' = remove the formula. */
  onApply: (text: string) => void;
}

/**
 * Formula Editor (prototype ALPACA_Daily_QC_Setup_v20.html) — one popup for a field's
 * Calculation ("CT =") or Acceptance Rule ("Pass when:"). Symbols and operators insert at
 * the cursor; the text is checked LIVE by the API (syntax, unknown symbols, a calc using its
 * own symbol, circular calcs) and OK stays disabled until it is valid.
 */
export default function BuilderFormulaDialog({ fields, index, kind, readOnly, onCancel, onApply }: Props) {
  const f = fields[index]!;
  const [text, setText] = useState((kind === 'calc' ? f.calc : f.accept) ?? '');
  const [check, setCheck] = useState<FormulaResult | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const seq = useRef(0);

  useEffect(() => {
    const n = ++seq.current;
    const t = setTimeout(() => {
      checkFormula(fields, index, kind, text)
        .then((r) => { if (n === seq.current) setCheck(r); })
        .catch(() => { if (n === seq.current) setCheck({ valid: false, message: 'Could not check the formula.', refs: [] }); });
    }, CHECK_DELAY_MS);
    return () => clearTimeout(t);
  }, [fields, index, kind, text]);

  const insert = (token: string) => {
    const el = areaRef.current;
    const a = el?.selectionStart ?? text.length;
    const z = el?.selectionEnd ?? text.length;
    const next = text.slice(0, a) + token + text.slice(z);
    setText(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.selectionStart = el.selectionEnd = a + token.length;
    });
  };

  const symbols = fields.filter((x) => x.symbol?.trim());
  const title = `${kind === 'calc' ? 'Calculation' : 'Acceptance Rule'} — ${labelOf(f)} (${f.symbol ?? ''})`;
  const valid = text.trim() === '' || check?.valid === true;
  const pending = text.trim() !== '' && check === null;

  const footer = readOnly
    ? <button className="ca-btn" onClick={onCancel}>Close</button>
    : (
      <>
        <button className="ca-btn" onClick={() => onApply('')}>Remove formula</button>
        <button className="ca-btn" onClick={onCancel}>Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={!valid || pending} onClick={() => onApply(text.trim())}>OK</button>
      </>
    );

  return (
    <AppDialog open title={title} width={880} onClose={onCancel} closeOnBackdrop={false} submitOnEnter={false} footer={footer} className="qcb-dlg">
      <div className="qcb-fx">
        <div className="qcb-fx-list">
          <div className="qcb-fx-head">Available Symbols</div>
          {symbols.map((x) => (
            <button key={x.symbol} type="button" className="qcb-fx-item" disabled={readOnly} onClick={() => insert(x.symbol!.trim())}>
              <b>{x.symbol}</b> <span className="qcb-muted">{labelOf(x)}</span>
            </button>
          ))}
        </div>
        <div className="qcb-fx-list">
          <div className="qcb-fx-head">Operators</div>
          {OPS.map(([op, hint]) => (
            <button key={op} type="button" className="qcb-fx-item" disabled={readOnly} onClick={() => insert(op)}>
              <b>{op.trim()}</b> {hint && <span className="qcb-muted">{hint}</span>}
            </button>
          ))}
        </div>
        <div>
          <label htmlFor="qcb-fx-text"><b>{kind === 'calc' ? `${f.symbol} =` : 'Pass when:'}</b></label>
          <textarea id="qcb-fx-text" ref={areaRef} className="qcb-fx-text" spellCheck={false} value={text} readOnly={readOnly}
            maxLength={kind === 'calc' ? 100 : 400} placeholder={kind === 'calc' ? 'Example: T2-T1' : 'Example: TEMP>=121'}
            onChange={(e) => setText(e.target.value)} />
          {text.trim() !== '' && check && (
            <div className={`qcb-fx-msg ${check.valid ? 'ok' : 'err'}`}>{check.message}</div>
          )}
          <div className="qcb-fx-help">
            {kind === 'calc'
              ? <>The result fills <b>{labelOf(f)}</b> automatically; the cell becomes read-only. Times are in minutes, so <code>T2-T1</code> gives elapsed minutes.</>
              : <>A true/false rule. The cell turns green when it passes and red when it fails. Checkboxes are 1 when checked, e.g. <code>SC=1</code>. Combine rules with <code>AND</code> / <code>OR</code>.</>}
          </div>
        </div>
      </div>
    </AppDialog>
  );
}
